import { Component, type ReactNode } from 'react';
import { translate, type Language } from '@/ui/i18n';
import { getSettings } from '@/store/settings';
import { isChunkLoadError, reloadForStaleChunk } from '@/ui/staleChunks';

interface State {
  error: Error | null;
}

/**
 * Catches render errors of a page so one broken view doesn't blank the whole app.
 * A running ride is safe: the session snapshots to IndexedDB every 10 s and is offered for recovery.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    if (isChunkLoadError(error) && reloadForStaleChunk()) return; // new deploy: fetch the new build
    console.error('[VeloStube] page crashed', error);
  }

  override componentDidUpdate(prev: { resetKey?: string }): void {
    // Navigating to another page clears the error.
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const lang = getSettings().language as Language;
    const t = (k: Parameters<typeof translate>[1]) => translate(lang, k);
    return (
      <div className="card" role="alert" data-testid="error-boundary">
        <h2>{t('error.title')}</h2>
        <p className="dim">{t('error.hint')}</p>
        <pre className="mono small" style={{ whiteSpace: 'pre-wrap' }}>
          {error.message}
        </pre>
        <div className="row">
          <button className="primary" onClick={() => window.location.reload()}>
            {t('error.reload')}
          </button>
          <a className="btn" href="/">
            {t('nav.home')}
          </a>
        </div>
      </div>
    );
  }
}
